import { api, queryString, type Items, type Paginated } from '@sentinel/web-core';
import type {
  OperationalThresholds,
  OperationalThresholdsUpdate,
  OrganizationContract,
  SmtpSettings,
  SmtpSettingsUpdate,
  SmtpTestResult,
  Territory,
} from '@sentinel/shared';

export interface OrganizationModuleRow {
  key: string;
  phase: string;
  isCore: boolean;
  implemented: boolean;
  /** Under contract (decided by the platform): `none` cannot be switched on here. */
  entitlement: 'included' | 'trial' | 'none';
  trialEndsAt: string | null;
  enabled: boolean;
  changedAt: string | null;
  /** Asked for by the organization, while the console has not answered yet. */
  requestedAt: string | null;
}

export interface OrganizationProfile {
  id: string;
  slug: string;
  name: string;
  kind: string;
  defaultLocale: 'it' | 'en';
  timezone: string;
  coordinateFormat: string;
  googleSignIn: 'disabled' | 'optional' | 'required';
  googleDomainOnly: boolean;
  emailDomains: string[];
  memberCount: number;
  enabledModuleCount: number;
  territory: Territory | null;
}

export interface StatusDefinitionRow {
  id: string;
  domain: string;
  key: string;
  labels: Record<string, string>;
  tone: string;
  sortOrder: number;
  isReserved: boolean;
}

export interface AuditEntryRow {
  id: string;
  occurredAt: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actor: { id: string; displayName: string } | null;
  platformAccessId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
}

export const configurationEndpoints = {
  modules: () => api<Items<OrganizationModuleRow>>('/modules'),
  toggleModule: (key: string, enabled: boolean) =>
    api<Items<OrganizationModuleRow>>(`/modules/${key}`, { method: 'PUT', body: { enabled } }),
  requestModule: (key: string, note: string) =>
    api<{ status: 'ok' }>(`/modules/${key}/requests`, { method: 'POST', body: { note } }),
  contract: () => api<OrganizationContract>('/organization/contract'),
  organization: () => api<OrganizationProfile>('/organization'),
  updateOrganization: (
    body: Partial<
      Pick<
        OrganizationProfile,
        | 'name'
        | 'kind'
        | 'defaultLocale'
        | 'timezone'
        | 'coordinateFormat'
        | 'googleSignIn'
        | 'googleDomainOnly'
        | 'territory'
      >
    >,
  ) => api<OrganizationProfile>('/organization', { method: 'PATCH', body }),
  smtp: () => api<SmtpSettings>('/organization/smtp'),
  updateSmtp: (body: SmtpSettingsUpdate) =>
    api<SmtpSettings>('/organization/smtp', { method: 'PUT', body }),
  testSmtp: (to: string) =>
    api<SmtpTestResult>('/organization/smtp/test', { method: 'POST', body: { to } }),
  thresholds: () => api<OperationalThresholds>('/organization/thresholds'),
  updateThresholds: (body: OperationalThresholdsUpdate) =>
    api<OperationalThresholds>('/organization/thresholds', { method: 'PUT', body }),
  states: () => api<Items<StatusDefinitionRow>>('/status-definitions'),
  createState: (body: {
    domain: string;
    key: string;
    labels: { it: string; en: string };
    tone: string;
  }) => api<StatusDefinitionRow>('/status-definitions', { method: 'POST', body }),
  audit: (params: { action?: string | undefined; page?: number; pageSize?: number }) =>
    api<Paginated<AuditEntryRow>>(`/audit${queryString(params)}`),
};
